"use client";

import * as React from "react";
import type { Matcher } from "react-day-picker";
import { ko } from "react-day-picker/locale";
import { cn } from "../../lib/utils";
import { Button as ShadcnButton } from "../ui/button";
import { Button } from "./Button";
import { Calendar } from "./Calendar";
import { DrawerContent, DrawerProvider, useDrawer } from "./Drawer";
import { Typo } from "./Typo";

interface DatePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  disabledDates?: Matcher | Matcher[];
  startMonth?: Date;
  endMonth?: Date;
  className?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "날짜 선택",
  disabled = false,
  disabledDates,
  startMonth,
  endMonth,
  className,
}: DatePickerProps) {
  return (
    <DrawerProvider>
      <DatePickerTrigger
        date={value}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
      />
      <DrawerContent className="p-5 pb-10">
        <DatePickerCalendarContent
          date={value}
          onDateChange={onChange}
          disabledDates={disabledDates}
          startMonth={startMonth}
          endMonth={endMonth}
        />
      </DrawerContent>
    </DrawerProvider>
  );
}

function DatePickerTrigger({
  date,
  placeholder,
  disabled,
  className,
}: {
  date: Date | undefined;
  placeholder: string;
  disabled: boolean;
  className?: string;
}) {
  const { open, isOpen } = useDrawer();

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const weekday = date.toLocaleDateString("ko-KR", { weekday: "long" });

    return `${year}.${month}.${day} ${weekday}`;
  };

  return (
    <ShadcnButton
      variant="outline"
      onClick={open}
      className={cn(
        "justify-between",
        isOpen && "!border-violet-500 bg-violet-50 text-violet-500",
        "shadow-none",
        className,
      )}
      disabled={disabled}
    >
      <Typo.ButtonText size="medium">{date ? formatDate(date) : placeholder}</Typo.ButtonText>
    </ShadcnButton>
  );
}

function DatePickerCalendarContent({
  date,
  onDateChange,
  disabledDates,
  startMonth,
  endMonth,
}: {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  disabledDates?: Matcher | Matcher[];
  startMonth?: Date;
  endMonth?: Date;
}) {
  const { close } = useDrawer();
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(date);

  React.useEffect(() => {
    setSelectedDate(date);
  }, [date]);

  const handleConfirm = () => {
    onDateChange(selectedDate);
    close();
  };

  return (
    <>
      <Calendar
        mode="single"
        locale={ko}
        selected={selectedDate}
        defaultMonth={date}
        captionLayout="dropdown"
        onSelect={setSelectedDate}
        className="w-full"
        disabled={disabledDates}
        startMonth={startMonth}
        endMonth={endMonth}
      />
      <Button className="mt-6 w-full" onClick={handleConfirm}>
        확인
      </Button>
    </>
  );
}
